/**
 * FloatingPager — fixed prev/next chevrons on the left/right viewport
 * edges for desktop visitors browsing paginated pages.
 *
 * Hidden on mobile/tablet (<lg) to keep content unobstructed and hidden
 * at the first/last page to avoid no-op clicks.
 *
 * Owner directive 2026-05-19: visitors deep in a paginated stack (e.g.
 * testimonials page 9 of 88) shouldn't have to scroll back to the
 * bottom paginator to advance. These floating buttons sit at hand height
 * on either side of the screen so paging is always one click away.
 *
 * Designed to wrap ANY page's pagination shape — caller controls when
 * the buttons are active and what each click does.
 *
 * Props:
 *   onPrev          fn       fired when ◀ is clicked (only when !atFirst)
 *   onNext          fn       fired when ▶ is clicked (only when !atLast)
 *   atFirst         bool     hide ◀ when true
 *   atLast          bool     hide ▶ when true
 *   isJa            bool     localizes aria-label / title
 *   accent          string   themable next-button color (default purple)
 *   testIdPrefix    string   stable data-testids
 */
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function FloatingPager({
  onPrev,
  onNext,
  atFirst = false,
  atLast = false,
  isJa = false,
  accent = "#5B2E83",
  testIdPrefix = "floating-pager",
}) {
  return (
    <>
      {!atFirst && (
        <button
          type="button"
          onClick={onPrev}
          data-testid={`${testIdPrefix}-prev`}
          aria-label={isJa ? "前のページ" : "Previous page"}
          title={isJa ? "前のページ" : "Previous page"}
          className="hidden lg:flex fixed left-4 xl:left-8 top-1/2 -translate-y-1/2 z-40 h-14 w-14 items-center justify-center rounded-full bg-white text-[#0F1D2D] ring-1 ring-[#E2E8F0] shadow-[0_18px_40px_-12px_rgba(15,29,45,0.25)] hover:bg-[#F1F5F9] hover:scale-110 hover:shadow-[0_24px_56px_-12px_rgba(15,29,45,0.35)] transition-all"
        >
          <ChevronLeft className="h-7 w-7" strokeWidth={2.5} />
        </button>
      )}
      {!atLast && (
        <button
          type="button"
          onClick={onNext}
          data-testid={`${testIdPrefix}-next`}
          aria-label={isJa ? "次のページ" : "Next page"}
          title={isJa ? "次のページ" : "Next page"}
          className="hidden lg:flex fixed right-4 xl:right-8 top-1/2 -translate-y-1/2 z-40 h-14 w-14 items-center justify-center rounded-full text-white hover:scale-110 transition-all"
          style={{
            backgroundColor: accent,
            boxShadow: `0 18px 40px -12px ${accent}99`,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = `0 24px 56px -12px ${accent}cc`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = `0 18px 40px -12px ${accent}99`;
          }}
        >
          <ChevronRight className="h-7 w-7" strokeWidth={2.5} />
        </button>
      )}
    </>
  );
}
